import i18n from "../config/i18n";
import { ApiResponse } from "../types/common";

/**
 * Check if a string looks like a backend error code (UPPER_SNAKE_CASE)
 * Examples: "INVALID_CREDENTIALS", "BRAND_NOT_FOUND", "ORDER_NOT_FOUND"
 */
function isErrorCode(value: string): boolean {
  return /^[A-Z][A-Z0-9_]+$/.test(value.trim());
}

/**
 * Translate an error code to the current language.
 * - If the string is an error code and translation exists → return translated string
 * - If the string is an error code but no translation → return generic error in current language
 * - If the string is NOT an error code (legacy English) → return as-is
 */
function translateError(message: string): string {
  if (isErrorCode(message)) {
    const key = `apiErrors.${message}`;
    if (i18n.exists(key)) {
      return i18n.t(key);
    }
    // Error code exists but not yet translated — use generic fallback
    return i18n.t("apiErrors.GENERIC_ERROR");
  }
  // Legacy English string from backend — pass through unchanged
  return message;
}

export function formatApiError(error: unknown | ApiResponse): string {
  if (!error) return i18n.t("apiErrors.GENERIC_ERROR");
  if (typeof error === "string") return translateError(error);

  if (typeof error === "object") {
    // ApiResponse from backend
    if ("isSuccess" in error && error.isSuccess === false) {
      const apiError = error as ApiResponse;
      if (Array.isArray(apiError.errors) && apiError.errors.length > 0)
        return apiError.errors.map(translateError).join(", ");
      if (apiError.message) return translateError(apiError.message);
    }

    // Axios error
    if (
      "response" in error &&
      error.response &&
      typeof error.response === "object"
    ) {
      interface AxiosErrorResponse {
        data?: {
          errors?: string[];
          message?: string;
          error?: string;
        };
        status?: number;
      }
      const response = error.response as AxiosErrorResponse;

      if (response.data) {
        if (
          Array.isArray(response.data.errors) &&
          response.data.errors.length > 0
        ) {
          return response.data.errors.map(translateError).join(", ");
        }
        if (response.data.message) {
          return translateError(response.data.message);
        }
        if (response.data.error) {
          return translateError(response.data.error);
        }
      }

      // Status-based fallbacks using translation keys
      switch (response.status) {
        case 400:
          return i18n.t("apiErrors.INVALID_REQUEST");
        case 401:
          return i18n.t("apiErrors.INVALID_CREDENTIALS");
        case 403:
          return i18n.t("apiErrors.ACCESS_DENIED");
        case 404:
          return i18n.t("apiErrors.NOT_FOUND");
        case 429:
          return i18n.t("apiErrors.TOO_MANY_REQUESTS");
        case 500:
          return i18n.t("apiErrors.SERVER_ERROR");
        default:
          return i18n.t("apiErrors.GENERIC_ERROR");
      }
    }

    // Standard Error object
    if ("message" in error && typeof error.message === "string") {
      return translateError(error.message);
    }
  }

  return i18n.t("apiErrors.GENERIC_ERROR");
}
